import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';
import {
  getSettings, updateSettings, changePassword,
  adminGetUsers, adminCreateUser, adminUpdateUser, adminResetPassword,
  adminGetDoctors, adminUpdateDoctor,
  adminGetClinic, adminUpdateClinic,
  adminGetInvoiceSettings, adminUpdateInvoiceSettings,
} from '@/lib/api';
import BillingServicesTab from '@/components/Billing/BillingServicesTab';
import {
  User, Lock, Building2, Users, Stethoscope, Receipt,
  CheckCircle, AlertTriangle, Plus, Pencil, KeyRound,
  ToggleLeft, ToggleRight, Save, RefreshCw, ShieldCheck, FileEdit,
} from 'lucide-react';
import {
  getPermissionRoleTemplates, getUserPermissions, updateUserPermissions,
} from '@/lib/api';
import { usePermission } from '@/hooks/usePermission';
import { PrescriptionLayoutConfig } from '@/types/prescription';
import { DEFAULT_PRESCRIPTION_LAYOUT } from '@/hooks/usePrescriptionLayout';

// ─── Toast helper ─────────────────────────────────────────────────────────────

function useNotify() {
  const [note, setNote] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);
  return { note, ok: (msg: string) => setNote({ type: 'success', msg }), err: (msg: string) => setNote({ type: 'error', msg }) };
}

function Notify({ note }: { note: { type: 'success' | 'error'; msg: string } | null }) {
  if (!note) return null;
  return (
    <Alert className={cn('mb-4', note.type === 'success' ? 'border-success/30 bg-success/5' : 'border-destructive/30 bg-destructive/5')}>
      {note.type === 'success'
        ? <CheckCircle className="h-4 w-4 text-success" />
        : <AlertTriangle className="h-4 w-4 text-destructive" />}
      <AlertDescription className={note.type === 'success' ? 'text-success' : 'text-destructive'}>{note.msg}</AlertDescription>
    </Alert>
  );
}

// ─── Shared field ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, desc, children }: { label: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
      {children}
    </div>
  );
}

// ─── 1. Profile tab ───────────────────────────────────────────────────────────

function ProfileTab() {
  const { currentUser } = useAuth();
  const { note, ok, err } = useNotify();
  const [form, setForm] = useState({ name: '', email: '', phone: '', specialty: 'General Medicine', license: '', signature: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    getSettings(currentUser.doctor_id, currentUser.clinic_id)
      .then(s => {
        if (s?.profile) setForm({
          name:      s.profile.name      || currentUser.name || '',
          email:     s.profile.email     || currentUser.email || '',
          phone:     s.profile.phone     || '',
          specialty: s.profile.specialty || 'General Medicine',
          license:   s.profile.license   || '',
          signature: s.profile.signature || '',
        });
      }).catch(() => {});
  }, [currentUser?.doctor_id]);

  const save = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setSaving(true);
    try {
      await updateSettings(currentUser.doctor_id, currentUser.clinic_id, {
        doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id,
        profile_name: form.name, profile_email: form.email, profile_phone: form.phone,
        profile_specialty: form.specialty, profile_license: form.license, profile_signature: form.signature,
      });
      ok('Profile saved');
    } catch { err('Failed to save profile'); }
    finally { setSaving(false); }
  };

  const s = (k: keyof typeof form) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="max-w-xl space-y-4">
      <Notify note={note} />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full Name"><Input value={form.name} onChange={e => s('name')(e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={e => s('email')(e.target.value)} /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={e => s('phone')(e.target.value)} /></Field>
        <Field label="Specialty">
          <Select value={form.specialty} onValueChange={s('specialty')}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {['General Medicine','Cardiology','Dermatology','Pediatrics','Orthopedics','Gynecology','ENT','Neurology','Psychiatry','Ophthalmology'].map(sp => (
                <SelectItem key={sp} value={sp}>{sp}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Medical License No."><Input value={form.license} onChange={e => s('license')(e.target.value)} className="col-span-2" /></Field>
      </div>
      <Field label="Signature (appears on prescriptions)">
        <Textarea value={form.signature} onChange={e => s('signature')(e.target.value)} rows={2} />
      </Field>
      <Button onClick={save} disabled={saving} size="sm">
        {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
        Save Profile
      </Button>
    </div>
  );
}

// ─── 2. Password tab ──────────────────────────────────────────────────────────

function PasswordTab() {
  const { note, ok, err } = useNotify();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.next !== form.confirm) { err('New passwords do not match'); return; }
    if (form.next.length < 6) { err('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await changePassword(form.current, form.next);
      ok('Password changed'); setForm({ current: '', next: '', confirm: '' });
    } catch (e: any) { err(e?.message || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xs space-y-4">
      <Notify note={note} />
      <Field label="Current Password"><PasswordInput value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} /></Field>
      <Field label="New Password"><PasswordInput value={form.next} onChange={e => setForm(f => ({ ...f, next: e.target.value }))} /></Field>
      <Field label="Confirm New Password"><PasswordInput value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} /></Field>
      <div className="flex items-center justify-end">
        <Button onClick={save} disabled={saving || !form.current || !form.next} size="sm">
          {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
          Change Password
        </Button>
      </div>
    </div>
  );
}

// ─── 3. Preferences tab ───────────────────────────────────────────────────────

function PreferencesTab() {
  const { currentUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { note, ok, err } = useNotify();
  const [autoSave, setAutoSave] = useState(true);
  const [notifications, setNotifications] = useState(true);
  const [printFormat, setPrintFormat] = useState<'a4' | 'letter' | 'compact'>('a4');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    getSettings(currentUser.doctor_id, currentUser.clinic_id).then(s => {
      if (!s) return;
      if (s.preferences?.autoSave   !== undefined) setAutoSave(!!s.preferences.autoSave);
      if (s.preferences?.notifications !== undefined) setNotifications(!!s.preferences.notifications);
      if (s.preferences?.printFormat)  setPrintFormat(s.preferences.printFormat);
    }).catch(() => {});
  }, [currentUser?.doctor_id]);

  const save = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setSaving(true);
    try {
      await updateSettings(currentUser.doctor_id, currentUser.clinic_id, {
        doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id,
        theme, language: 'en', notifications, auto_save: autoSave, print_format: printFormat,
      });
      ok('Preferences saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md space-y-2">
      <Notify note={note} />
      <Row label="Theme" desc="Application colour scheme">
        <Select value={theme} onValueChange={v => setTheme(v as any)}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="light">Light</SelectItem>
            <SelectItem value="dark">Dark</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Print Format" desc="Default paper size for prescriptions">
        <Select value={printFormat} onValueChange={v => setPrintFormat(v as any)}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="a4">A4</SelectItem>
            <SelectItem value="letter">Letter</SelectItem>
            <SelectItem value="compact">Compact</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Auto-save" desc="Save forms automatically while typing">
        <Switch checked={autoSave} onCheckedChange={setAutoSave} />
      </Row>
      <Row label="Notifications" desc="In-app reminders and alerts">
        <Switch checked={notifications} onCheckedChange={setNotifications} />
      </Row>
      <Button onClick={save} disabled={saving} size="sm" className="mt-2">
        {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
        Save Preferences
      </Button>
    </div>
  );
}

// ─── 3b. Prescription Layout tab ──────────────────────────────────────────────

const HIDEABLE_PRESCRIPTION_SECTIONS: { id: string; label: string; desc: string }[] = [
  { id: 'vitals',         label: 'Vital Signs',    desc: 'BP, weight, temperature, etc.' },
  { id: 'investigations', label: 'Investigations', desc: 'Lab tests and imaging' },
  { id: 'advice',         label: 'Advice',         desc: 'General advice to patient' },
  { id: 'notes',          label: 'Quick Notes',    desc: 'Free-form notes' },
  { id: 'next-visit',     label: 'Next Visit',     desc: 'Follow-up date and reminders' },
];

function PrescriptionLayoutTab() {
  const { currentUser } = useAuth();
  const { note, ok, err } = useNotify();
  const [layout, setLayout] = useState<PrescriptionLayoutConfig>(DEFAULT_PRESCRIPTION_LAYOUT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    getSettings(currentUser.doctor_id, currentUser.clinic_id).then(s => {
      if (s?.prescriptionLayout) setLayout(s.prescriptionLayout);
    }).catch(() => {});
  }, [currentUser?.doctor_id]);

  const toggleSection = (id: string, visible: boolean) => {
    setLayout(prev => ({
      ...prev,
      hiddenSections: visible
        ? prev.hiddenSections.filter(s => s !== id)
        : [...prev.hiddenSections, id],
    }));
  };

  const updateCustomField = (key: string, changes: Partial<{ label: string; enabled: boolean }>) => {
    setLayout(prev => ({
      ...prev,
      customFields: prev.customFields.map(cf => cf.key === key ? { ...cf, ...changes } : cf),
    }));
  };

  const save = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setSaving(true);
    try {
      await updateSettings(currentUser.doctor_id, currentUser.clinic_id, {
        doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id,
        prescription_layout: layout,
      });
      ok('Prescription layout saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md space-y-6">
      <Notify note={note} />

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Sections</p>
        <p className="text-xs text-muted-foreground mb-2">Hide sections you don't use. Complaints, Diagnosis, and Medications are always shown.</p>
        {HIDEABLE_PRESCRIPTION_SECTIONS.map(s => (
          <Row key={s.id} label={s.label} desc={s.desc}>
            <Switch
              checked={!layout.hiddenSections.includes(s.id)}
              onCheckedChange={(checked) => toggleSection(s.id, checked)}
            />
          </Row>
        ))}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Custom Fields</p>
        <p className="text-xs text-muted-foreground mb-2">Add up to 3 extra free-text fields with your own labels.</p>
        {layout.customFields.map((cf, idx) => (
          <div key={cf.key} className="flex items-center gap-2 py-2 border-b last:border-0">
            <Switch
              checked={cf.enabled}
              onCheckedChange={(checked) => updateCustomField(cf.key, { enabled: checked })}
            />
            <Input
              value={cf.label}
              onChange={(e) => updateCustomField(cf.key, { label: e.target.value })}
              placeholder={`Custom field ${idx + 1} label`}
              className="h-8 text-sm"
              maxLength={50}
            />
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} size="sm">
        {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
        Save Layout
      </Button>
    </div>
  );
}

// ─── 4. Clinic Info tab (admin) ───────────────────────────────────────────────

function ClinicTab() {
  const { note, ok, err } = useNotify();
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '', gstin: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetClinic().then(c => {
      if (c) setForm({ name: c.name || '', address: c.address || '', phone: c.phone || '', email: c.email || '', gstin: c.gstin || '' });
    }).catch(() => {});
  }, []);

  const s = (k: keyof typeof form) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { err('Clinic name is required'); return; }
    setSaving(true);
    try {
      await adminUpdateClinic(form);
      ok('Clinic info saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl space-y-4">
      <Notify note={note} />
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><Field label="Clinic Name *"><Input value={form.name} onChange={e => s('name')(e.target.value)} /></Field></div>
        <Field label="Phone"><Input value={form.phone} onChange={e => s('phone')(e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={e => s('email')(e.target.value)} /></Field>
        <Field label="GSTIN"><Input value={form.gstin} onChange={e => s('gstin')(e.target.value)} placeholder="22AAAAA0000A1Z5" /></Field>
      </div>
      <Field label="Address">
        <Textarea value={form.address} onChange={e => s('address')(e.target.value)} rows={3} />
      </Field>
      <Button onClick={save} disabled={saving} size="sm">
        {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
        Save Clinic Info
      </Button>
    </div>
  );
}

// ─── 5. Staff (Users) tab (admin) ─────────────────────────────────────────────

const ROLES = ['doctor', 'staff', 'admin'] as const;
type Role = typeof ROLES[number];

const ROLE_COLORS: Record<string, string> = {
  admin:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  doctor: 'bg-primary/10 text-primary',
  staff:  'bg-success/10 text-success',
};

function StaffTab() {
  const { currentUser } = useAuth();
  const { isOwner } = usePermission();
  const { note, ok, err } = useNotify();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add user modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'staff' as Role });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit modal
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'staff' as Role, is_owner: false });
  const [editSaving, setEditSaving] = useState(false);

  // Reset pw modal
  const [resetUser, setResetUser] = useState<any | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Generated temp password (shown once after create/reset)
  const [generatedPw, setGeneratedPw] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const u = await adminGetUsers();
      setUsers(u ?? []);
    } catch { err('Failed to load staff'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setAddError('');
    if (!addForm.name || !addForm.email) { setAddError('Name and email are required'); return; }
    if (addForm.password && addForm.password.length < 6) { setAddError('Password must be at least 6 characters'); return; }
    setAddSaving(true);
    try {
      const created = await adminCreateUser(addForm.password ? addForm : { ...addForm, password: undefined });
      ok('User created'); setAddOpen(false);
      if (created?.temp_password) setGeneratedPw({ email: addForm.email, password: created.temp_password });
      setAddForm({ name: '', email: '', password: '', role: 'staff' }); load();
    } catch (e: any) { setAddError(e?.message || 'Failed to create user'); }
    finally { setAddSaving(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const data: any = { name: editForm.name };
      if (isOwner) data.is_owner = editForm.is_owner;
      await adminUpdateUser(editUser.id, data);
      ok('User updated'); setEditUser(null); load();
    } catch (e: any) { err(e?.message || 'Failed to update user'); }
    finally { setEditSaving(false); }
  };

  const toggleActive = async (u: any) => {
    try {
      await adminUpdateUser(u.id, { is_active: u.is_active ? 0 : 1 });
      ok(`User ${u.is_active ? 'deactivated' : 'activated'}`); load();
    } catch { err('Failed'); }
  };

  const handleReset = async () => {
    if (!resetUser) return;
    setResetSaving(true);
    try {
      const result = await adminResetPassword(resetUser.id, resetPw || undefined);
      ok('Password reset');
      if (result?.temp_password) setGeneratedPw({ email: resetUser.email, password: result.temp_password });
      setResetUser(null); setResetPw('');
    } catch (e: any) { err(e?.message || 'Failed'); }
    finally { setResetSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Notify note={note} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{users.length} user{users.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={() => { setAddForm({ name: '', email: '', password: '', role: 'staff' }); setAddError(''); setAddOpen(true); }}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add User
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b">
                {['Name', 'Email', 'Role', 'Linked Doctor', 'Status', ''].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className={cn('border-b last:border-0', i % 2 === 0 ? '' : 'bg-muted/10', !u.is_active && 'opacity-50')}>
                  <td className="px-3 py-2.5 font-medium">{u.name}</td>
                  <td className="px-3 py-2.5 text-muted-foreground text-xs">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium capitalize', ROLE_COLORS[u.role] ?? 'bg-muted text-muted-foreground')}>{u.role}</span>
                    {!!u.is_owner && (
                      <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Owner</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {u.doctor_name || (!u.doctor_id && u.role === 'doctor'
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" title="No doctor profile linked">Unlinked</span>
                      : u.doctor_id ? '' : '—')}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('text-xs', u.is_active ? 'text-green-600' : 'text-muted-foreground')}>{u.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Edit" onClick={() => { setEditUser(u); setEditForm({ name: u.name, role: u.role, is_owner: !!u.is_owner }); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Reset password" onClick={() => { setResetUser(u); setResetPw(''); }}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title={
                          u.id === currentUser?.id
                            ? "Cannot deactivate your own account"
                            : u.is_owner
                            ? "Cannot deactivate the owner/admin"
                            : u.is_active ? 'Deactivate' : 'Activate'
                        }
                        onClick={() => toggleActive(u)}
                        disabled={u.is_owner || u.id === currentUser?.id}
                      >
                        {u.is_active ? <ToggleRight className={cn("w-3.5 h-3.5", u.is_owner || u.id === currentUser?.id ? "text-muted-foreground" : "text-green-600")} /> : <ToggleLeft className="w-3.5 h-3.5 text-muted-foreground" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add user */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name *"><Input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Email *"><Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} /></Field>
            <Field label="Password (optional)">
              <PasswordInput value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-muted-foreground mt-1">If left blank, a temporary password is generated and shown once. The user must change it on first login.</p>
            </Field>
            <Field label="Role">
              <Select value={addForm.role} onValueChange={v => setAddForm(f => ({ ...f, role: v as Role }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {addForm.role === 'doctor' && (
              <p className="text-xs text-muted-foreground">A doctor profile will be created automatically for this user.</p>
            )}
            {addError && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{addError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addSaving}>{addSaving ? 'Creating…' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={!!editUser} onOpenChange={v => { if (!v) setEditUser(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit User — {editUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name"><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Role">
              <Input value={editForm.role} className="capitalize" disabled />
              <p className="text-xs text-muted-foreground mt-1">Role cannot be changed after creation.</p>
            </Field>
            {editForm.role === 'doctor' && !editUser?.doctor_id && (
              <p className="text-xs text-muted-foreground">A doctor profile will be created automatically for this user.</p>
            )}
            {isOwner && (
              <Row label="Owner" desc="Owners can manage staff permissions and other owners">
                <Switch
                  checked={editForm.is_owner}
                  disabled={editUser?.id === currentUser?.id}
                  onCheckedChange={v => setEditForm(f => ({ ...f, is_owner: v }))}
                />
              </Row>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} disabled={editSaving}>Cancel</Button>
            <Button onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetUser} onOpenChange={v => { if (!v) setResetUser(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Reset Password — {resetUser?.name}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Field label="New Password (optional)">
              <PasswordInput value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-muted-foreground mt-1">The user must change this password on next login.</p>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)} disabled={resetSaving}>Cancel</Button>
            <Button onClick={handleReset} disabled={resetSaving || (resetPw.length > 0 && resetPw.length < 6)}>{resetSaving ? 'Resetting…' : 'Reset'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated temp password */}
      <Dialog open={!!generatedPw} onOpenChange={v => { if (!v) setGeneratedPw(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Temporary Password</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-muted-foreground">
              Share this with <span className="font-medium text-foreground">{generatedPw?.email}</span>. It won't be shown again.
            </p>
            <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm">
              {generatedPw?.password}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setGeneratedPw(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── 6. Doctors tab (admin) ───────────────────────────────────────────────────

function DoctorsTab() {
  const { note, ok, err } = useNotify();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDoc, setEditDoc] = useState<any | null>(null);
  const [form, setForm] = useState({ name: '', specialty: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setDoctors((await adminGetDoctors()) ?? []); }
    catch { err('Failed to load doctors'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (d: any) => { setEditDoc(d); setForm({ name: d.name, specialty: d.specialty || '', phone: d.phone || '', email: d.email || '' }); setFormError(''); };

  const [modalOpen, setModalOpen] = useState(false);

  const handleSave = async () => {
    if (!editDoc) return;
    setFormError('');
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    try {
      await adminUpdateDoctor(editDoc.id, form);
      ok('Doctor updated');
      setModalOpen(false); load();
    } catch (e: any) { setFormError(e?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const SPECIALTIES = ['General Medicine','Cardiology','Dermatology','Pediatrics','Orthopedics','Gynecology','ENT','Neurology','Psychiatry','Ophthalmology','Other'];

  return (
    <div className="space-y-4">
      <Notify note={note} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{doctors.length} doctor{doctors.length !== 1 ? 's' : ''}</p>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : doctors.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">No doctors added yet.</div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b">
                {['Name', 'Specialty', 'Phone', 'Email', ''].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {doctors.map((d, i) => (
                <tr key={d.id} className={cn('border-b last:border-0', i % 2 === 0 ? '' : 'bg-muted/10')}>
                  <td className="px-3 py-2.5 font-medium">{d.name}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{d.specialty || '—'}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{d.phone || '—'}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{d.email || '—'}</td>
                  <td className="px-3 py-2.5">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { openEdit(d); setModalOpen(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={v => { if (!v) setModalOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Doctor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Specialty">
              <Select value={form.specialty} onValueChange={v => setForm(f => ({ ...f, specialty: v }))}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone"><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></Field>
            {formError && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{formError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── 7. Billing tab (admin) ───────────────────────────────────────────────────

function BillingTab({ clinicId, doctorId }: { clinicId: string; doctorId?: string }) {
  const { note, ok, err } = useNotify();
  const [inv, setInv] = useState({
    prefix: 'INV-',
    tax_rate: '0',
    tax_label: 'GST',
    payment_terms_text: '',
    payment_terms_days: '30',
    footer_text: '',
    currency: 'INR',
    auto_send: false,
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'services' | 'invoice'>('services');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);

  useEffect(() => {
    adminGetInvoiceSettings().then(d => {
      if (d) setInv({
        prefix: d.prefix || 'INV-',
        tax_rate: String(d.tax_rate ?? 0),
        tax_label: d.tax_label || 'GST',
        payment_terms_text: d.payment_terms_text || '',
        payment_terms_days: String(d.payment_terms_days ?? 30),
        footer_text: d.footer_text || '',
        currency: d.currency || 'INR',
        auto_send: !!d.auto_send,
      });
    }).catch(() => {});
  }, []);

  const saveInv = async () => {
    setSaving(true);
    try {
      await adminUpdateInvoiceSettings({
        ...inv,
        tax_rate: parseFloat(inv.tax_rate) || 0,
        payment_terms_days: parseInt(inv.payment_terms_days) || 30,
      });
      ok('Invoice settings saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleAddService = () => {
    setSelectedService(null);
    setShowServiceModal(true);
  };

  const handleCloseServiceModal = () => {
    setShowServiceModal(false);
    setSelectedService(null);
  };

  const handleEditService = (service: any) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  const handleServiceSaved = () => {
    setShowServiceModal(false);
    setSelectedService(null);
  };

  return (
    <div className="space-y-4">
      <Notify note={note} />
      {/* Sub-section toggle */}
      <div className="flex rounded-md border overflow-hidden text-xs w-fit">
        {(['services', 'invoice'] as const).map(s => (
          <button key={s} onClick={() => setActiveSection(s)}
            className={cn('px-4 py-1.5 capitalize transition-colors',
              activeSection === s ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted text-muted-foreground')}>
            {s === 'services' ? 'Services Catalog' : 'Invoice Settings'}
          </button>
        ))}
      </div>

      {activeSection === 'services' ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Manage billing services for your clinic</p>
            <Button size="sm" onClick={handleAddService}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Service
            </Button>
          </div>
          <BillingServicesTab
            clinicId={clinicId}
            doctorId={doctorId}
            canManage={true}
            onNotification={(type, msg) => type === 'success' ? ok(msg) : err(msg)}
            showServiceModal={showServiceModal}
            onCloseServiceModal={handleCloseServiceModal}
            selectedService={selectedService}
            onServiceSaved={handleServiceSaved}
            onEditService={handleEditService}
          />
        </div>
      ) : (
        <div className="max-w-md space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Invoice Number Prefix"><Input value={inv.prefix} onChange={e => setInv(f => ({ ...f, prefix: e.target.value }))} placeholder="INV-" /></Field>
            <Field label="Tax Label"><Input value={inv.tax_label} onChange={e => setInv(f => ({ ...f, tax_label: e.target.value }))} placeholder="GST" /></Field>
            <Field label="Default Tax Rate (%)"><Input type="number" value={inv.tax_rate} onChange={e => setInv(f => ({ ...f, tax_rate: e.target.value }))} min="0" max="100" /></Field>
            <Field label="Currency">
              <Select value={inv.currency} onValueChange={v => setInv(f => ({ ...f, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="space-y-1.5">
            <Label>Payment Terms</Label>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Days">
                <Input type="number" value={inv.payment_terms_days} onChange={e => setInv(f => ({ ...f, payment_terms_days: e.target.value }))} min="0" max="90" placeholder="30" />
              </Field>
              <Field label="Display Text">
                <Input value={inv.payment_terms_text} onChange={e => setInv(f => ({ ...f, payment_terms_text: e.target.value }))} placeholder="e.g. Due on receipt" />
              </Field>
            </div>
          </div>
          <Field label="Invoice Footer Text">
            <Textarea value={inv.footer_text} onChange={e => setInv(f => ({ ...f, footer_text: e.target.value }))} rows={2} placeholder="Thank you for your visit…" />
          </Field>
          <Row label="Auto-send Invoices" desc="Automatically send invoice emails to customers">
            <Switch checked={inv.auto_send} onCheckedChange={v => setInv(f => ({ ...f, auto_send: v }))} />
          </Row>
          <Button onClick={saveInv} disabled={saving} size="sm">
            {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save Invoice Settings
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── 7. Staff Permissions tab ─────────────────────────────────────────────────

// Module → relevant actions shown in the permission matrix
const MODULE_ACTIONS: Record<string, { key: string; label: string }[]> = {
  patients:      [{ key: 'read', label: 'View' }, { key: 'write', label: 'Edit' }, { key: 'delete', label: 'Delete' }],
  appointments:  [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
  prescriptions: [{ key: 'read', label: 'View' }, { key: 'write', label: 'Create/Edit' }],
  billing:       [{ key: 'read', label: 'View' }, { key: 'write', label: 'Create/Edit' }, { key: 'cancel', label: 'Cancel' }, { key: 'refund', label: 'Refund' }, { key: 'discount', label: 'Discount' }],
  queue:         [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
  ipd:           [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
  reports:       [{ key: 'read', label: 'View All' }, { key: 'own_only', label: 'Own Only' }],
  settings:      [{ key: 'read', label: 'View' }, { key: 'write', label: 'Edit' }],
  users:         [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
};

const MODULE_LABELS: Record<string, string> = {
  patients: 'Patients', appointments: 'Appointments', prescriptions: 'Prescriptions', billing: 'Billing', queue: 'Queue',
  ipd: 'IPD', reports: 'Reports', settings: 'Settings', users: 'Staff & Users',
};

// Module → plan feature it requires (to hide modules not on this tenant's plan)
// 'patients' is a special sentinel meaning "always available" (matches
// backend permissionService.js's MODULE_PLAN_MAP) — not a real plan feature.
const MODULE_PLAN_FEATURE: Record<string, string> = {
  patients: 'patients', appointments: 'patients', prescriptions: 'patients', billing: 'patients', queue: 'patients', settings: 'patients',
  ipd: 'ipd', reports: 'reports', users: 'role_access',
};

function StaffPermissionsTab() {
  const { currentUser } = useAuth();
  const { isOwner, hasFeature } = usePermission();
  const visibleModules = Object.keys(MODULE_ACTIONS).filter(m => {
    const feature = MODULE_PLAN_FEATURE[m];
    return feature === 'patients' || hasFeature(feature);
  });
  const { note, ok, err } = useNotify();

  const [users, setUsers]       = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [perms, setPerms]       = useState<any | null>(null);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  // Form state for selected user
  const [templateId, setTemplateId] = useState<string>('none');
  const [overrides, setOverrides]   = useState<Record<string, 0 | 1>>({});

  useEffect(() => {
    Promise.all([adminGetUsers(), getPermissionRoleTemplates()])
      .then(([u, t]) => { setUsers(u ?? []); setTemplates(t ?? []); })
      .catch(() => err('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const selectUser = async (u: any) => {
    setSelected(u);
    setPerms(null);
    try {
      const data = await getUserPermissions(u.id);
      setPerms(data);
      setTemplateId(data.role_template_id ?? 'none');
      setOverrides(data.overrides ?? {});
    } catch { err('Failed to load user permissions'); }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateUserPermissions(selected.id, {
        role_template_id: templateId === 'none' ? null : templateId,
        overrides,
      });
      ok('Permissions saved');
    } catch (e: any) { err(e?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const roleBadge: Record<string, string> = {
    admin:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    doctor: 'bg-primary/10 text-primary',
    staff:  'bg-success/10 text-success',
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="flex gap-6 h-full">
      {/* Left: user list */}
      <div className="w-56 shrink-0 border rounded-lg overflow-y-auto">
        {users.map(u => (
          <button key={u.id} onClick={() => selectUser(u)}
            className={cn(
              'w-full text-left px-3 py-2.5 border-b last:border-b-0 flex items-start gap-2 transition-colors',
              selected?.id === u.id ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-muted',
            )}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{u.name}</p>
              <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', roleBadge[u.role] ?? 'bg-gray-100 text-gray-600')}>
                {u.role}
              </span>
            </div>
          </button>
        ))}
        {!users.length && <p className="text-xs text-muted-foreground p-3">No staff found</p>}
      </div>

      {/* Right: permission editor */}
      <div className="flex-1 min-w-0">
        <Notify note={note} />
        {!selected && (
          <div className="text-sm text-muted-foreground mt-6">Select a staff member to manage their permissions.</div>
        )}
        {selected && (
          <div className="space-y-5">
            <div>
              <p className="font-medium">{selected.name}</p>
              <p className="text-xs text-muted-foreground">{selected.email}</p>
            </div>

            {/* Role template */}
            <div className="space-y-1.5">
              <Label>Role Template</Label>
              <Select value={templateId} onValueChange={setTemplateId} disabled={!isOwner}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select template…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Base permissions come from the selected template.</p>
            </div>

            {/* Permission matrix */}
            <div className="space-y-3">
              <Label>Permission Overrides</Label>
              {!isOwner && (
                <p className="text-xs text-amber-600">Only owners can change permission overrides.</p>
              )}
              <p className="text-xs text-muted-foreground">
                Checked items come from the role template. Click a box to override it for this user;
                highlighted boxes are overridden — click "Reset" to fall back to the template default.
              </p>
              <div className="space-y-2">
                {visibleModules.map(module => (
                  <div key={module} className="rounded-lg border p-3">
                    <p className="text-sm font-medium mb-2">{MODULE_LABELS[module] ?? module}</p>
                    <div className="flex flex-wrap gap-2">
                      {MODULE_ACTIONS[module].map(({ key: action, label }) => {
                        const permKey = `${module}:${action}`;
                        const current = overrides[permKey];
                        const fromTemplate = perms?.effective_permissions?.[module]?.includes(action) ?? false;
                        const checked = current === 1 ? true : current === 0 ? false : fromTemplate;
                        const isOverridden = current !== undefined;
                        return (
                          <div key={permKey} className={cn(
                            'flex items-center gap-1.5 rounded-md border px-2 py-1.5',
                            isOverridden ? 'border-primary/40 bg-primary/5' : 'border-transparent bg-muted/40',
                          )}>
                            <Switch
                              checked={checked}
                              disabled={!isOwner}
                              onCheckedChange={v => setOverrides(prev => ({ ...prev, [permKey]: v ? 1 : 0 }))}
                            />
                            <span className="text-xs">{label}</span>
                            {isOverridden && (
                              <button className="text-[10px] text-muted-foreground underline"
                                onClick={() => { const next = { ...overrides }; delete next[permKey]; setOverrides(next); }}
                                disabled={!isOwner}>
                                Reset
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {isOwner && (
              <Button onClick={handleSave} disabled={saving} size="sm">
                {saving ? 'Saving…' : 'Save Permissions'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Section = 'profile' | 'password' | 'preferences' | 'prescription-layout' | 'clinic' | 'staff' | 'doctors' | 'billing' | 'permissions';

const ALL_SECTIONS: { id: Section; label: string; icon: React.ElementType; adminOnly?: boolean; feature?: string; requiresDoctor?: boolean }[] = [
  { id: 'profile',     label: 'Profile',       icon: User,      requiresDoctor: true },
  { id: 'password',    label: 'Password',       icon: Lock },
  { id: 'preferences', label: 'Preferences',    icon: RefreshCw, requiresDoctor: true },
  { id: 'prescription-layout', label: 'Prescription Layout', icon: FileEdit, requiresDoctor: true },
  { id: 'clinic',      label: 'Clinic Info',    icon: Building2,   adminOnly: true },
  { id: 'staff',       label: 'Staff',          icon: Users,       adminOnly: true, feature: 'role_access' },
  { id: 'doctors',     label: 'Doctors',        icon: Stethoscope, adminOnly: true },
  { id: 'billing',     label: 'Billing',        icon: Receipt,     adminOnly: true },
  { id: 'permissions', label: 'Permissions',    icon: ShieldCheck, adminOnly: true, feature: 'role_access' },
];

export default function SettingsPage() {
  const { currentUser } = useAuth();
  const { hasFeature } = usePermission();
  const isAdmin = currentUser?.role === 'admin';
  const [section, setSection] = useState<Section>('profile');

  useEffect(() => { document.title = 'Settings | OneCheckUp'; }, []);

  const visible = ALL_SECTIONS.filter(s =>
    (!s.adminOnly || isAdmin) && (!s.feature || hasFeature(s.feature)) && (!s.requiresDoctor || !!currentUser?.doctor_id)
  );

  useEffect(() => {
    if (!visible.some(s => s.id === section) && visible.length) setSection(visible[0].id);
  }, [visible.map(s => s.id).join(','), section]);

  const groups = [
    { label: 'My Account', items: visible.filter(s => !s.adminOnly) },
    ...(isAdmin ? [{ label: 'Clinic Admin', items: visible.filter(s => s.adminOnly) }] : []),
  ];

  return (
    <div className="flex h-full">
      {/* Left nav */}
      <aside className="w-48 shrink-0 border-r flex flex-col py-4 gap-1 px-2 overflow-y-auto">
        {groups.map(g => (
          <div key={g.label} className="mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-2 mb-1">{g.label}</p>
            {g.items.map(item => (
              <button key={item.id} onClick={() => setSection(item.id)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                  section === item.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}>
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </div>
        ))}
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-6 py-6">
        <h2 className="text-base font-semibold mb-4 capitalize">
          {ALL_SECTIONS.find(s => s.id === section)?.label}
        </h2>

        {section === 'profile'     && <ProfileTab />}
        {section === 'password'    && <PasswordTab />}
        {section === 'preferences' && <PreferencesTab />}
        {section === 'prescription-layout' && <PrescriptionLayoutTab />}
        {section === 'clinic'      && isAdmin && <ClinicTab />}
        {section === 'staff'       && isAdmin && <StaffTab />}
        {section === 'doctors'     && isAdmin && <DoctorsTab />}
        {section === 'billing'     && isAdmin && currentUser?.clinic_id && (
          <BillingTab clinicId={currentUser.clinic_id} doctorId={currentUser.doctor_id} />
        )}
        {section === 'permissions' && isAdmin && <StaffPermissionsTab />}
      </main>
    </div>
  );
}
